/**
 * WindPlan AI / Energy Bot API Client Service
 */

import {
  ChatStreamRequest,
  StreamEventChunk,
  EnergyBotApiError,
  StandardApiResponse,
} from "./types";

export * from "./types";

export const ENERGY_BOT_BASE_URL = "https://wesoft.co.jp/energy-bot";

export interface StreamChatOptions {
  signal?: AbortSignal;
  onStart?: () => void;
  onToken?: (token: string) => void;
  onDone?: () => void;
}

/**
 * Initiates an SSE Stream chat request to WindPlan AI gateway
 */
export async function streamChat(
  payload: ChatStreamRequest,
  options: StreamChatOptions = {}
): Promise<void> {
  const url = `${ENERGY_BOT_BASE_URL}/api/v1/chat/stream`;
  const { signal, onStart, onToken, onDone } = options;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new EnergyBotApiError("ABORTED", "Request aborted");
    }
    throw new EnergyBotApiError(
      "NETWORK_ERROR",
      err?.message || "Network request failed"
    );
  }

  if (!response.ok || !response.body) {
    if (response.status === 429) {
      throw new EnergyBotApiError(
        "RATE_LIMIT_EXCEEDED",
        "Rate limit exceeded (HTTP 429)",
        429
      );
    }
    if (response.status === 400 || response.status === 403) {
      throw new EnergyBotApiError(
        "CAPTCHA_FAILED",
        "Captcha verification failed (HTTP 400/403)",
        response.status
      );
    }
    throw new EnergyBotApiError(
      "HTTP_ERROR",
      `HTTP Error ${response.status}`,
      response.status
    );
  }

  // Trigger onStart callback as soon as valid headers & stream body are confirmed
  if (onStart) {
    onStart();
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? ""; // keep incomplete tail in buffer

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;

      const jsonStr = trimmed.slice(5).trim();
      if (!jsonStr) continue;

      try {
        const event = JSON.parse(jsonStr) as StreamEventChunk;
        if (event.done) {
          break;
        }

        if (event.content && onToken) {
          onToken(event.content);
        }
      } catch {
        // Silently skip malformed frame JSON
      }
    }
  }

  if (onDone) {
    onDone();
  }
}

/**
 * Standard Non-Streaming Query API (Fallback)
 */
export async function queryChat(
  payload: ChatStreamRequest
): Promise<StandardApiResponse> {
  const url = `${ENERGY_BOT_BASE_URL}/api/v1/chat/query`;

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new EnergyBotApiError("RATE_LIMIT_EXCEEDED", "Rate limit", 429);
    }
    throw new EnergyBotApiError(
      "HTTP_ERROR",
      `HTTP ${response.status}`,
      response.status
    );
  }

  return response.json();
}
